import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTrashCan } from 'react-icons/fa6';
import { useResusContext } from '../Resus/ResusContext';
import { useCPRLog } from './CPRLog';
import ProtocolsDialog from './ProtocolsDialog';
import { saveCurrentState } from './CprState/storage';
import ProtocolAttachments from './ProtocolAttachments';
import { useTranslation } from 'react-i18next';

import './Protocols.css';
import { getHospitalData } from './PerHospitalResusConfig';

interface Attachment {
  name: string;
  file: string;
}

interface ProtocolData {
  id: string;
  name: string;
  attachments?: Attachment[];
}

const ProtocolHeader = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ProtocolName = styled.div`
  font-weight: bold;
  color: #103C6E;
  text-align: right;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #103C6E;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const Protocols: React.FC = () => {
  const { protocols, setProtocols, hospital } = useResusContext();
  const { addEntry } = useCPRLog();
  const { t } = useTranslation(['resus']);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const emergencyProtocols = getHospitalData(hospital).protocols
  const getProtocolName = (protocolId: string): string | undefined => {
    for (const section of emergencyProtocols.emergencyProtocols) {
      // Ensure section.protocols exists and is an array before finding
      const protocol = Array.isArray(section.protocols) ? section.protocols.find(p => p.id === protocolId) : undefined;
      if (protocol) {
        return protocol.name;
      }
    }
    return undefined;
  };

  const handleRemoveProtocol = (protocolId: string) => {
    const newProtocols = protocols.filter(p => p !== protocolId);
    const removedName = getProtocolName(protocolId);

    if (removedName) {
      addEntry({
        timestamp: new Date().toISOString(),
        text: t('protocols.removedSingle', { name: removedName }),
        type: 'openingConditions',
        isImportant: false
      });
    }

    setProtocols(newProtocols);
    saveCurrentState({
      resusContext: {
        protocols: newProtocols
      }
    }, 'resusContext');
  };

  const handleProtocolsUpdate = (newProtocols: string[]) => {
    const addedProtocols = newProtocols.filter(p => !protocols.includes(p));
    const removedProtocols = protocols.filter(p => !newProtocols.includes(p));

    const logParts: string[] = [];

    if (addedProtocols.length > 0) {
      const addedNames = addedProtocols
        .map(getProtocolName)
        .filter((name): name is string => name !== undefined);

      if (addedNames.length === 1) {
        logParts.push(t('protocols.addedSingle', { name: addedNames[0] }));
      } else if (addedNames.length > 1) {
        logParts.push(t('protocols.addedMultiple', { names: addedNames.join(', ') }));
      }
    }

    if (removedProtocols.length > 0) {
      const removedNames = removedProtocols
        .map(getProtocolName)
        .filter((name): name is string => name !== undefined);

      if (removedNames.length === 1) {
        logParts.push(t('protocols.removedSingle', { name: removedNames[0] }));
      } else if (removedNames.length > 1) {
        logParts.push(t('protocols.removedMultiple', { names: removedNames.join(', ') }));
      }
    }

    if (logParts.length > 0) {
      addEntry({
        timestamp: new Date().toISOString(),
        text: logParts.join('. '),
        type: 'openingConditions',
        isImportant: false
      });
    }

    setProtocols(newProtocols);
    setIsDialogOpen(false);

    saveCurrentState({
      resusContext: {
        protocols: newProtocols
      }
    }, 'resusContext');
  };

  const getProtocolFiles = () => {
    const files: JSX.Element[] = [];

    protocols.forEach((protocolId) => {
      let foundProtocol: ProtocolData | undefined;
      for (const section of emergencyProtocols.emergencyProtocols) {
        // Ensure section.protocols exists and is an array before finding
        foundProtocol = Array.isArray(section.protocols) ? section.protocols.find(p => p.id === protocolId) : undefined;
        if (foundProtocol) break;
      }

      if (foundProtocol) {
        files.push(
          <div key={protocolId} className="protocol-item">
            <ProtocolHeader>
              <RemoveButton
                onClick={() => handleRemoveProtocol(protocolId)}
                title={t('buttons.removeProtocol')}
              >
                <FaTrashCan />
              </RemoveButton>
              <ProtocolName>{foundProtocol.name}</ProtocolName>
            </ProtocolHeader>
            <ProtocolAttachments
              name={foundProtocol.name}
              attachments={foundProtocol.attachments || []} // Pass the attachments array
              hideTitle={true}
            />
          </div>
        );
      }
    });

    return files;
  };

  return (
    <div className="cpr-protocols-container" style={{ textAlign: 'center' }}>
      <button
        type="button"
        className="btn primary-mashlom-btn"
        data-testid="protocols-choose-protocols"
        style={{ width: '250px', height: '48px' }}
        onClick={() => setIsDialogOpen(true)}
      >
        {t('protocols.chooseProtocols')}
      </button>

      {protocols.length > 0 && getProtocolFiles().length === 0 && (
        <div className="no-files-message">{t('protocols.noAttachments')}</div>
      )}

      {protocols.length > 0 && getProtocolFiles().length > 0 && (
        <div className="protocol-body">
          {getProtocolFiles()}
        </div>
      )}

      <ProtocolsDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        protocols={protocols}
        onUpdate={handleProtocolsUpdate}
      />
    </div>
  );
};

export default Protocols;
